import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { BookOpen, Plus, ExternalLink, Trash2, Upload, FileText, AtSign, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { resolveUrl as resolveCoverUrl } from "@/components/CoverUpload";
import CinematicBook from "@/components/CinematicBook";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  // Publishers shouldn't see the author dashboard — bounce them to /publisher.
  useEffect(() => {
    if (user?.role === "publisher") navigate("/publisher", { replace: true });
  }, [user, navigate]);
  const [books, setBooks] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", theme: "", genres: "", description: "" });
  const [importing, setImporting] = useState(false);
  const [slugDraft, setSlugDraft] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);
  const importRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, sh] = await Promise.all([
        api.get("/books"),
        api.get("/shared-with-me").catch(() => ({ data: [] })),
      ]);
      setBooks(data);
      setShared(sh.data || []);
    } catch (e) {
      toast.error("Failed to load books");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.slug) setSlugDraft(user.slug); }, [user?.slug]);

  const saveSlug = async () => {
    if (!slugDraft || slugDraft === user.slug) return;
    setSavingSlug(true);
    try {
      const { data } = await api.patch("/users/me/slug", { slug: slugDraft });
      setUser({ ...user, slug: data.slug });
      toast.success("URL updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSavingSlug(false);
    }
  };

  const create = async (e) => {
    e?.preventDefault();
    setCreating(true);
    try {
      const payload = {
        title: form.title,
        theme: form.theme,
        genres: form.genres.split(",").map((s) => s.trim()).filter(Boolean),
        description: form.description,
      };
      const { data } = await api.post("/books", payload);
      toast.success("Book created");
      setOpen(false);
      navigate(`/books/${data.book_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create book");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (book) => {
    if (!confirm(`Delete "${book.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/books/${book.book_id}`);
      toast.success("Book deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const onImportPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okExt = /\.(docx|pdf)$/i.test(file.name);
    if (!okExt) {
      toast.error("Only .docx and .pdf files are supported");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Max 25MB");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/books/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported "${data.book.title}" — ${data.chapter_count} chapter(s)`);
      navigate(`/books/${data.book.book_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bf-bg)" }} data-testid="dashboard-page">
      <SiteNav />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
        <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-start mb-12">
          <div>
            <div className="label-eyebrow mb-3">Your shelf</div>
            <h1 className="font-serif text-5xl">My books</h1>
            <p className="mt-4 max-w-lg text-base" style={{ color: "var(--bf-text-muted)" }}>
              Pick up where you left off, or start something new. Your last edited book is always front and centre.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <input
                ref={importRef}
                type="file"
                accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={onImportPick}
                data-testid="dashboard-import-input"
              />
              <Button
                variant="outline"
                className="h-11 px-5"
                onClick={() => importRef.current?.click()}
                disabled={importing}
                data-testid="dashboard-import-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> {importing ? "Importing…" : "Import .docx / .pdf"}
              </Button>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="text-white h-11 px-6" style={{ background: "var(--bf-primary)" }} data-testid="dashboard-new-book-btn">
                    <Plus className="w-4 h-4 mr-2" /> New book
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="new-book-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Start a new book</DialogTitle>
                    <DialogDescription>You can change everything later — including the title.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={create} className="space-y-4">
                    <div>
                      <Label htmlFor="title" className="mb-1.5 block">Title</Label>
                      <Input id="title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-book-title" />
                    </div>
                    <div>
                      <Label htmlFor="theme" className="mb-1.5 block">Theme</Label>
                      <Input id="theme" placeholder="Loss and rediscovery, the human cost of ambition…" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} data-testid="new-book-theme" />
                    </div>
                    <div>
                      <Label htmlFor="genres" className="mb-1.5 block">Genres (comma separated)</Label>
                      <Input id="genres" placeholder="literary fiction, thriller" value={form.genres} onChange={(e) => setForm({ ...form, genres: e.target.value })} data-testid="new-book-genres" />
                    </div>
                    <div>
                      <Label htmlFor="description" className="mb-1.5 block">Description</Label>
                      <Textarea id="description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-book-description" />
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={creating} className="text-white" style={{ background: "var(--bf-primary)" }} data-testid="new-book-submit-btn">
                        {creating ? "Creating..." : "Create book"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="hidden lg:block" data-testid="dashboard-cinematic-wrap">
            <CinematicBook
              authorName={user?.name}
              bookTitle={books[0]?.title}
            />
          </div>
        </div>

        {/* Custom author URL */}
        {user && (
          <section
            className="mb-10 p-5 rounded-lg flex flex-col sm:flex-row gap-4 items-start sm:items-center"
            style={{ background: "white", border: "1px solid var(--bf-border)" }}
            data-testid="author-url-card"
          >
            <AtSign className="w-5 h-5 flex-shrink-0" style={{ color: "var(--bf-primary)" }} />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg leading-tight">Your author URL</p>
              <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>
                Lowercase letters/numbers/hyphens. Visible at {window.location.origin}/<strong>{user.slug}</strong>.
              </p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <span className="text-sm self-center" style={{ color: "var(--bf-text-muted)" }}>/</span>
              <Input
                value={slugDraft}
                onChange={(e) => setSlugDraft(e.target.value.toLowerCase())}
                className="w-44"
                data-testid="dashboard-slug-input"
              />
              <Button
                onClick={saveSlug}
                disabled={savingSlug || !slugDraft || slugDraft === user.slug}
                className="text-white"
                style={{ background: "var(--bf-primary)" }}
                data-testid="dashboard-slug-save"
              >
                Save
              </Button>
            </div>
          </section>
        )}

        {/* Shared with me */}
        {shared.length > 0 && (
          <section className="mb-12" data-testid="shared-with-me-section">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5" style={{ color: "var(--bf-accent)" }} />
              <h2 className="font-serif text-2xl">Shared with me</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shared.map((b) => (
                <Link
                  to={`/books/${b.book_id}`}
                  key={b.book_id}
                  className="p-5 rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-sm"
                  style={{ background: "white", border: "1px solid var(--bf-border)" }}
                  data-testid={`shared-book-${b.book_id}`}
                >
                  <span className="label-eyebrow">{b.my_role}</span>
                  <h3 className="font-serif text-xl mt-2 mb-1 leading-tight">{b.title}</h3>
                  <p className="text-xs" style={{ color: "var(--bf-text-muted)" }}>by {b.owner_name}</p>
                </Link>
              ))}
            </div>
          </section>
        )}


        {loading ? (
          <div className="text-sm" style={{ color: "var(--bf-text-muted)" }}>Loading…</div>
        ) : books.length === 0 ? (
          <div
            className="p-16 text-center rounded-lg"
            style={{ background: "var(--bf-surface)", border: "1px dashed var(--bf-border)" }}
            data-testid="dashboard-empty-state"
          >
            <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--bf-accent)" }} />
            <h2 className="font-serif text-3xl mb-2">No books yet</h2>
            <p className="text-sm mb-6" style={{ color: "var(--bf-text-muted)" }}>
              Tap &quot;New book&quot; to start your first manuscript.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dashboard-books-grid">
            {books.map((b) => (
              <div
                key={b.book_id}
                className="p-6 rounded-lg transition-all hover:-translate-y-1 hover:shadow-sm"
                style={{ background: "white", border: "1px solid var(--bf-border)" }}
                data-testid={`book-card-${b.book_id}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <span className="label-eyebrow">{b.status}</span>
                  <button
                    onClick={() => remove(b)}
                    className="opacity-60 hover:opacity-100 transition-opacity"
                    data-testid={`book-delete-${b.book_id}`}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Link to={`/books/${b.book_id}`} className="block">
                  {b.cover_image_url && (
                    <img
                      src={resolveCoverUrl(b.cover_image_url)}
                      alt="cover"
                      className="w-full h-44 object-cover rounded mb-4"
                      style={{ border: "1px solid var(--bf-border)" }}
                      data-testid={`book-cover-${b.book_id}`}
                    />
                  )}
                  <h3 className="font-serif text-2xl mb-2 leading-tight" data-testid={`book-title-${b.book_id}`}>
                    {b.title}
                  </h3>
                  <p className="text-sm line-clamp-2 mb-4" style={{ color: "var(--bf-text-muted)" }}>
                    {b.description || b.theme || "No description yet."}
                  </p>
                </Link>
                <div className="flex flex-wrap gap-2 mb-4">
                  {(b.genres || []).slice(0, 3).map((g) => (
                    <span key={g} className="text-xs px-2 py-1 rounded" style={{ background: "var(--bf-surface)", color: "var(--bf-text-muted)" }}>
                      {g}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2 pt-3 border-t" style={{ borderColor: "var(--bf-border)" }}>
                  <Link to={`/books/${b.book_id}`} className="flex-1" data-testid={`book-open-${b.book_id}`}>
                    <Button variant="outline" className="w-full" size="sm">Open</Button>
                  </Link>
                  {b.is_public && (
                    <a
                      href={`/${encodeURIComponent(b.slug.split("/")[0])}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`book-public-link-${b.book_id}`}
                    >
                      <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
